import {WorkQueueActionType, loadWorkItems, viewWorkItemImage, getAppEntryType} from 'ApplicationEntry/ducks/workQueue';
import {takeEvery, all, put, call} from 'redux-saga/effects';
import {
  getWorkItem,
  getNextImage,
  getWorkItemChildren,
  getWorkInformation,
  getEnrollmentApplication,
  getPolicyRecord,
  getProducts,
  isAllstateAgent,
  getAssignedWorkItem,
  lockWorkItem,
  getACHIndicator,
  getPACData
} from 'ApplicationEntry/api/awdApi';
import {WorkQueueItem} from 'ApplicationEntry/models/WorkQueueItem';
import {AsyncResult, Loaded} from 'Core/models/Async';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {ApplicationModeType} from 'ApplicationEntry/models/ApplicationModeType';
import {clearApplication, setApplicationType} from 'ApplicationEntry/ducks/application';
import * as E from 'fp-ts/lib/Either';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {WorkInstanceFieldValue} from 'ApplicationEntry/models/WorkInstanceFieldValue';
import {Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import * as CD from 'ApplicationEntry/ducks/caseDetails';

type sourceInstance = {
  id: string;
};

type LockWorkItemModel = {
  message: string;
  workInstance: {
    lockedTo: string;
  };
};

function* getWorkItemsSaga() {
  yield takeEvery(WorkQueueActionType.GET_WORK_ITEMS, function*() {
    let response: AsyncResult<{
      work: WorkSelected;
      messages: {message: string; responseSeverity: string}[];
    }> = yield call(getAssignedWorkItem); // Attempt to get an assigned work item with the highest priority.

    if (E.isRight(response)) {
      if (
        response.right.messages.some(
          responseMessage =>
            responseMessage.message ===
            'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
        )
      ) {
        response = yield call(getWorkItem);
      }
    }

    if (E.isRight(response)) {
      if (
        !response.right.messages.some(
          responseMessage =>
            responseMessage.message ===
            'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
        )
      ) {
        if (response.right.work.id !== undefined && response.right.work.id !== null && response.right.work.id !== '') {
          const lockWorkItemKey: AsyncResult<LockWorkItemModel> = yield lockWorkItem(response.right.work.id);

          if (E.isRight(lockWorkItemKey)) {
            const workItemChildrenResponse: AsyncResult<{
              awdWorkKeyId: string;
              id: string;
              sourceInstances: sourceInstance[];
              policyNumber: string;
              typeNameContent: string;
              businessAreaNameContent: string;
              queueNameContent: string;
              statusNameContent: string;
            }> = yield getWorkItemChildren(response.right.work.id);

            if (E.isRight(workItemChildrenResponse)) {
              const sourceInstanceId = pipe(
                NEA.fromArray(workItemChildrenResponse.right.sourceInstances),
                O.map(NEA.head),
                O.map(si => si.id),
                O.getOrElse<string | undefined>(() => undefined)
              );

              const workItem: WorkQueueItem = {
                awdworkkeyid: workItemChildrenResponse.right.awdWorkKeyId,
                date: response.right.work.date,
                summary: response.right.work.summary,
                time: response.right.work.time,
                sourceInstanceId,
                policyNumber: workItemChildrenResponse.right.policyNumber,
                typeNameContent: workItemChildrenResponse.right.typeNameContent,
                businessAreaNameContent: workItemChildrenResponse.right.businessAreaNameContent,
                queueNameContent: workItemChildrenResponse.right.queueNameContent,
                statusNameContent: workItemChildrenResponse.right.statusNameContent,
              };

              yield put(loadWorkItems(new Loaded([workItem])));
            } else if (workItemChildrenResponse.left.key === 'Unauthorized') {
              yield put(push(ApplicationEntryRouteConstants.AWDLogin));
            } else {
              yield put(loadWorkItems(new Loaded([])));
            }
          } else {
            yield put(loadWorkItems(new Loaded([])));
          }
        }
      } else {
        yield all([
          put(
            createSnackbar('There is nothing in the work queue right now. Talk to your supervisor or try again later.')
          ),
          put(push(ApplicationEntryRouteConstants.AppEntryLogin)),
        ]);
      }
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(loadWorkItems(new Loaded([])));
    }
  });
}

function* getWorkItemImageSaga() {
  yield takeEvery(WorkQueueActionType.VIEW_WORK_ITEM_IMAGE, function*({
    payload: {id},
  }: ReturnType<typeof viewWorkItemImage>) {
    const imageResponse: AsyncResult<Blob> = yield call(getNextImage, id);
    if (E.isRight(imageResponse)) {
      if (imageResponse.right.size > 1000) {
        const image = new Blob([imageResponse.right], {type: 'application/pdf'});
        const url: string = URL.createObjectURL(image);
        window.open(url);
      }
    } 
  });
}

type WorkSelected = {
  summary: string;
  date: string;
  id: string;
  time: string;
  businessAreaNameContent: string;
  typeNameContent: string;
  statusNameContent: string;
};

type WorkInstance = {
  id: string;
  businessAreaNameContent: string;
  typeNameContent: string;
  statusNameContent: string;
  queueNameContent: string;
  assignedTo: string;
  userId: string;
  lockedTo: string;
  priorityIncrease: number;
  fieldValues: {
    name: string;
    value: string;
  }[];
};

function* getAppEntryTypeSaga() {
  yield takeEvery(WorkQueueActionType.GET_APP_ENTRY_TYPE, function*({
    payload: {awdWorkKey, applicationType, workType},
  }: ReturnType<typeof getAppEntryType>) {
    const applicationInfoResponse: AsyncResult<{appEntryType: ApplicationType; mode: ApplicationModeType}> = yield call(
      getWorkInformation,
      awdWorkKey
    );

    let isSuccessResponse = true;
    if (E.isRight(applicationInfoResponse)) {
      let enrollmentData = {};
      let pacDataResultData = {};
      let beneficiaryInformation: object[] = [];
      let policyNumber = '';
      let products = [] as Product[];
      let caseNumber = '';
      let achIndicatorQuality;

      let {appEntryType, mode} = applicationInfoResponse.right;

      if (mode === ApplicationModeType.Quality) {
        isSuccessResponse = false;

        let temporaryApplicationType = appEntryType;
        let stateExclusivityIgnored = 'NY';
        let ignoreNonexclusive = false;

        if (temporaryApplicationType === ApplicationType.NewYork) {
          temporaryApplicationType = ApplicationType.Ordinary;
          stateExclusivityIgnored = 'NC,TN'; // Ignore products that are not exclusive to New York and have a known state exclusivity.
          ignoreNonexclusive = true; // Ignore products that do not have state exclusivity.
        }

        const productResponse: AsyncResult<Product[]> = yield call(
          getProducts,
          temporaryApplicationType,
          stateExclusivityIgnored,
          ignoreNonexclusive
        );

        if (E.isRight(productResponse)) {
          isSuccessResponse = true;
          products = productResponse.right;
        }

        let currentWorkResponse: AsyncResult<{
          work: WorkSelected;
          messages: {message: string; responseSeverity: string}[];
        }> = yield call(getAssignedWorkItem); // Attempt to get an assigned work item with the highest priority.

        if (E.isRight(currentWorkResponse)) {
          if (
            currentWorkResponse.right.messages.some(
              responseMessage =>
                responseMessage.message ===
                'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
            )
          ) {
            currentWorkResponse = yield call(getWorkItem);
          }
        }

        if (E.isRight(currentWorkResponse)) {
          const workItemChildrenResponse: AsyncResult<{workInstance: WorkInstance}> = yield getPolicyRecord(
            currentWorkResponse.right.work.id
          );

          if (E.isRight(workItemChildrenResponse)) {
            const workItemChildren = workItemChildrenResponse.right;

            const coCode = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.CoCode)
              ?.value;

            if (
              (coCode === '80' || coCode === '90') &&
              workItemChildrenResponse.right?.workInstance?.typeNameContent === 'CONVERT'
            ) {
              appEntryType = ApplicationType.Ordinary;
            }
            policyNumber = workItemChildren.workInstance.fieldValues.find(
              x => x.name === WorkInstanceFieldValue.PolicyNumber
            )?.value!;

            caseNumber = workItemChildren.workInstance.fieldValues.find(
              x => x.name === WorkInstanceFieldValue.CaseNumber
            )?.value!;

            const response: AsyncResult<{achCaseDetail: object}> = yield call(getACHIndicator, caseNumber);
              if (E.isRight(response)) {
                let achCaseDetail: any = response.right.achCaseDetail;
                achIndicatorQuality = achCaseDetail['achIndicator'];
                if (achCaseDetail['achIndicator'] === null) {
                  achIndicatorQuality = false;
                } else {
                  achIndicatorQuality = achCaseDetail['achIndicator'];
                }
                CD.getACHIndicatorValue(achIndicatorQuality);
                yield put(CD.changeCaseNumber(caseNumber));            
                
                if (achIndicatorQuality.valueOf().toString() === 'true'){
                  policyNumber = coCode + policyNumber;

                  const responseLookup: AsyncResult<{enrollment: object}> = yield call(
                    getEnrollmentApplication,
                    policyNumber
                  );

                  if (E.isRight(responseLookup)) {
                    
                    enrollmentData = responseLookup.right.enrollment;
                    const caseInformation = enrollmentData['caseInformation'];
                    const policyStatus = caseInformation.policyStatus;
                    const policy = caseInformation['policy'];
                    const eeiIndicator = policy['eeiIndicator'];
                    const clients = caseInformation['clients'];
                    const ssn = clients[0]['ssn'];
                    const firstName = clients[0]['firstName'];
                    const lastName = clients[0]['lastName'];
                    if (achIndicatorQuality.valueOf().toString() === 'true' && eeiIndicator.valueOf().toString() === 'EPC') {
                      const responseLookup1: AsyncResult<{pacDataResult: object}> = yield call(
                        getPACData,
                        ssn,
                        firstName,
                        lastName
                      );
                      if (E.isRight(responseLookup1)) {
                        pacDataResultData = responseLookup1.right.pacDataResult;
                      } else if (E.isLeft(responseLookup1) && responseLookup1.left.key === 'NotFound') {
                        sessionStorage.setItem('policyRecordNotFoundKey', 'policyRecordNotFoundValue');
                        yield all([
                          put(createSnackbar('POLICY RECORD NOT FOUND IN L70 SYSTEM')),
                          put(push(ApplicationEntryRouteConstants.WorkQueue)),
                        ]);
                        put(push(ApplicationEntryRouteConstants.WorkQueue));
                        window.location.href = ApplicationEntryRouteConstants.WorkQueue;
                      }
                      
                      beneficiaryInformation = enrollmentData['people'];
                      
                      if (policyStatus === 'PENDING') {
                        isSuccessResponse = true;

                        const agents = enrollmentData['caseInformation']['agents'] as object[];

                        for (let i = 0; i < agents.length; i++) {
                          const agentNumber = agents[i]['ahlAgentCode'];

                          const isAllstateAgentResponse: AsyncResult<boolean> = yield call(isAllstateAgent, agentNumber);

                          if (E.isRight(isAllstateAgentResponse)) {
                            agents[i]['allstateAffiliated'] = isAllstateAgentResponse.right;
                          }
                        }
                        sessionStorage.removeItem('policyRecordNotFoundKey');
                      } else if (policyStatus === 'DISPOSITIONED') {
                        yield all([
                          put(
                            setApplicationType(
                              appEntryType,
                              mode,
                              beneficiaryInformation,
                              enrollmentData,
                              policyNumber,
                              products,
                              achIndicatorQuality,
                              pacDataResultData
                            )
                          ),
                          put(createSnackbar('This policy has been issued. Please select a different enrollment')),
                          put(push(ApplicationEntryRouteConstants.WorkQueue)),
                        ]);
                      }
                    } else if (achIndicatorQuality.valueOf().toString() === 'true') {
                      
                    beneficiaryInformation = enrollmentData['people'];
                      if (policyStatus === 'PENDING') {
                        isSuccessResponse = true;

                        const agents = enrollmentData['caseInformation']['agents'] as object[];

                        for (let i = 0; i < agents.length; i++) {
                          const agentNumber = agents[i]['ahlAgentCode'];

                          const isAllstateAgentResponse: AsyncResult<boolean> = yield call(isAllstateAgent, agentNumber);

                          if (E.isRight(isAllstateAgentResponse)) {
                            agents[i]['allstateAffiliated'] = isAllstateAgentResponse.right;
                          }
                        }
                        sessionStorage.removeItem('policyRecordNotFoundKey');
                      } else if (policyStatus === 'DISPOSITIONED') {
                        yield all([
                          put(
                            setApplicationType(
                              appEntryType,
                              mode,
                              beneficiaryInformation,
                              enrollmentData,
                              policyNumber,
                              products,
                              achIndicatorQuality,
                              pacDataResultData
                            )
                          ),
                          put(createSnackbar('This policy has been issued. Please select a different enrollment')),
                          put(push(ApplicationEntryRouteConstants.WorkQueue)),
                        ]);
                      }
                    }
                  } else if (E.isLeft(responseLookup) && responseLookup.left.key === 'NotFound') {
                    sessionStorage.setItem('policyRecordNotFoundKey', 'policyRecordNotFoundValue');
                    yield all([
                      put(createSnackbar('POLICY RECORD NOT FOUND IN L70 SYSTEM')),
                      put(push(ApplicationEntryRouteConstants.WorkQueue)),
                    ]);
                    put(push(ApplicationEntryRouteConstants.WorkQueue));
                    window.location.href = ApplicationEntryRouteConstants.WorkQueue;
                  }
                } else if(achIndicatorQuality.valueOf().toString() === 'false'){
                  // achIndicatorQuality = true;
                  policyNumber = coCode + policyNumber;

                  const responseLookup: AsyncResult<{enrollment: object}> = yield call(
                    getEnrollmentApplication,
                    policyNumber
                  );

                  if (E.isRight(responseLookup)) {
                    
                    enrollmentData = responseLookup.right.enrollment;
                    const caseInformation = enrollmentData['caseInformation'];
                    const policyStatus = caseInformation.dateSigned;
                    
                    const beneficiaryInformation = enrollmentData['people'];
      
                    if (policyStatus === 'PENDING') {
                      isSuccessResponse = true;
      
                      const agents = enrollmentData['caseInformation']['agents'] as object[];
      
                      for (let i = 0; i < agents.length; i++) {
                        const agentNumber = agents[i]['ahlAgentCode'];
      
                        const isAllstateAgentResponse: AsyncResult<boolean> = yield call(isAllstateAgent, agentNumber);
      
                        if (E.isRight(isAllstateAgentResponse)) {
                          agents[i]['allstateAffiliated'] = isAllstateAgentResponse.right;
                        }
                      }
                      sessionStorage.removeItem('policyRecordNotFoundKey');
                    } else if (policyStatus === 'DISPOSITIONED') {
                      yield all([
                        put(
                          setApplicationType(
                            appEntryType,
                            mode,
                            beneficiaryInformation,
                            enrollmentData,
                            policyNumber,
                            products,
                            achIndicatorQuality,
                            pacDataResultData
                          )
                        ),
                        put(createSnackbar('This policy has been issued. Please select a different enrollment')),
                        put(push(ApplicationEntryRouteConstants.WorkQueue)),
                      ]);
                    }
                  } else if (E.isLeft(responseLookup) && responseLookup.left.key === 'NotFound') {
                    sessionStorage.setItem('policyRecordNotFoundKey', 'policyRecordNotFoundValue');
                    yield all([
                      put(createSnackbar('POLICY RECORD NOT FOUND IN L70 SYSTEM')),
                      put(push(ApplicationEntryRouteConstants.WorkQueue)),
                    ]);
                    put(push(ApplicationEntryRouteConstants.WorkQueue));
                    window.location.href = ApplicationEntryRouteConstants.WorkQueue;
                  }
                } 
              } else if (achIndicatorQuality === undefined) {
                achIndicatorQuality = false;
                policyNumber = coCode + policyNumber;

                const responseLookup: AsyncResult<{enrollment: object}> = yield call(
                  getEnrollmentApplication,
                  policyNumber
                );

                if (E.isRight(responseLookup)) {
                  
                  enrollmentData = responseLookup.right.enrollment;
                  const caseInformation = enrollmentData['caseInformation'];
                  const policyStatus = caseInformation.dateSigned;
                  beneficiaryInformation = enrollmentData['people'];
    
                  if (policyStatus === 'PENDING') {
                    isSuccessResponse = true;
    
                    const agents = enrollmentData['caseInformation']['agents'] as object[];
    
                    for (let i = 0; i < agents.length; i++) {
                      const agentNumber = agents[i]['ahlAgentCode'];
    
                      const isAllstateAgentResponse: AsyncResult<boolean> = yield call(isAllstateAgent, agentNumber);
    
                      if (E.isRight(isAllstateAgentResponse)) {
                        agents[i]['allstateAffiliated'] = isAllstateAgentResponse.right;
                      }
                    }
                    sessionStorage.removeItem('policyRecordNotFoundKey');
                  } else if (policyStatus === 'DISPOSITIONED') {
                    yield all([
                      put(
                        setApplicationType(
                          appEntryType,
                          mode,
                          beneficiaryInformation,
                          enrollmentData,
                          policyNumber,
                          products,
                          achIndicatorQuality,
                          pacDataResultData
                        )
                      ),
                      put(createSnackbar('This policy has been issued. Please select a different enrollment')),
                      put(push(ApplicationEntryRouteConstants.WorkQueue)),
                    ]);
                  }
                } else if (E.isLeft(responseLookup) && responseLookup.left.key === 'NotFound') {
                  sessionStorage.setItem('policyRecordNotFoundKey', 'policyRecordNotFoundValue');
                  yield all([
                    put(createSnackbar('POLICY RECORD NOT FOUND IN L70 SYSTEM')),
                    put(push(ApplicationEntryRouteConstants.WorkQueue)),
                  ]);
                  put(push(ApplicationEntryRouteConstants.WorkQueue));
                  window.location.href = ApplicationEntryRouteConstants.WorkQueue;
                }
              } else if (response.left.key === 'ServerError') { 
                yield put(CD.asyncCaseNumber(new Loaded(caseNumber)));
                yield all([
                  put(createSnackbar('ACH INDICATOR NOT FOUND IN L70 SYSTEM')),
                  put(push(ApplicationEntryRouteConstants.WorkQueue)),
                ]);
                put(push(ApplicationEntryRouteConstants.WorkQueue));
                window.location.href = ApplicationEntryRouteConstants.WorkQueue;
              }
          }
        }
      }

      if (isSuccessResponse && mode !== ApplicationModeType.Undefined && appEntryType !== ApplicationType.Undefined) {
        yield put(
          setApplicationType(appEntryType, mode, beneficiaryInformation, enrollmentData, policyNumber, products, achIndicatorQuality, pacDataResultData)
        );
      } else if (workType === 'CONVERT' && applicationType !== undefined) {
        if (applicationType === 'GROUP') {
          yield put(
            setApplicationType(
              applicationType!,
              ApplicationModeType.Entry,
              beneficiaryInformation,
              enrollmentData,
              policyNumber,
              products,
              achIndicatorQuality,
              pacDataResultData
            )
          );
        }
        if (applicationType === 'ORDINARY') {
          yield put(
            setApplicationType(
              applicationType!,
              ApplicationModeType.Entry,
              beneficiaryInformation,
              enrollmentData,
              policyNumber,
              products,
              achIndicatorQuality,
              pacDataResultData
            )
          );
        }
      } else {
        yield all([
          put(createSnackbar('Unable to retrieve data. Please, try again later.')),
          put(push(ApplicationEntryRouteConstants.WorkQueue)),
        ]);
      }
    } else if (applicationInfoResponse.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(clearApplication());
    }
  });
}

export default function*() {
  yield all([getWorkItemsSaga(), getWorkItemImageSaga(), getAppEntryTypeSaga()]);
}
